import React, { useEffect, useMemo, useState } from 'react';
import { ConfiguredField } from '../../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../../components/DynamicFieldRenderer';
import { safeArray } from '../../../components/ErrorBoundary';
import { adminAPI } from '../../../services/authService';
import {
  SECTION_CODES,
  STANDARD_RECOVERY_ROWS,
  STANDARD_TOTAL_ROWS,
  STANDARD_WORK_VALUE_ROWS,
  additionalRowsForSection,
  buildStandardComparisonRows,
  createAdditionalBillRow,
  formatIndianAmount,
  parseIndianAmountInput,
  recoveryRowLabelsWithRates,
  unionAdditionalBillRows,
} from '../../../utils/raBillDocumentRows';
import {
  isConfigurableSnapshot,
  isTemplateInputField,
  groupTemplateFieldsBySection,
  listEditableTemplateFields,
} from '../../../utils/raBillTemplateRuntime';
import './RABillDocument.css';

const IndianAmountInput = ({ value, onCommit, disabled }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const numeric = value === '' || value == null ? 0 : Number(value) || 0;
  const display = focused ? draft : formatIndianAmount(numeric);

  return (
    <input
      className="ra-amount-input"
      inputMode="decimal"
      disabled={disabled}
      value={display}
      onFocus={() => {
        setFocused(true);
        setDraft(value === '' || value == null ? '' : String(numeric));
      }}
      onChange={(event) => {
        const next = parseIndianAmountInput(event.target.value);
        setDraft(next === '' ? '' : String(next));
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseIndianAmountInput(draft);
        onCommit(parsed === '' ? 0 : parsed);
      }}
    />
  );
};

const ComparisonRow = ({
  row,
  onCurrentChange,
  onDescriptionChange,
  currentEditable,
  descriptionEditable = false,
  descriptionOptions = [],
  invalidDescription = false,
}) => {
  const isTotal = Boolean(row.isTotal);
  const cellClass = `amount${isTotal ? ' total-row' : ''}`;
  return (
    <tr>
      <td className={`desc-col${isTotal ? ' total-row' : ''}`} align="left" style={{ textAlign: 'left' }}>
        {descriptionEditable ? (
          <>
            <select
              className={`ra-inline-input${invalidDescription ? ' ra-field-invalid' : ''}`}
              style={{ textAlign: 'left' }}
              data-ra-field={`extra_row_${row.row_id || row.row_code}`}
              value={row.description || ''}
              onChange={(event) => {
                const selected = descriptionOptions.find((option) => option.label === event.target.value);
                onDescriptionChange({
                  description: selected?.label || event.target.value,
                  row_code: selected?.optionKey || row.row_code,
                });
              }}
            >
              <option value="">Select description</option>
              {descriptionOptions.map((option) => (
                <option key={option.id || option.optionKey} value={option.label}>{option.label}</option>
              ))}
              {row.description && !descriptionOptions.some((option) => option.label === row.description) ? (
                <option value={row.description}>{row.description}</option>
              ) : null}
            </select>
            {descriptionOptions.length === 0 ? (
              <p className="muted ra-row-hint">Add names in RA Bill Master</p>
            ) : null}
          </>
        ) : row.label}
      </td>
      <td className={`${cellClass} current-col${currentEditable ? ' editable-cell' : ''}`}>
        {currentEditable ? (
          <IndianAmountInput value={row.current_value} onCommit={onCurrentChange} />
        ) : (
          <span className="readonly-amount">{formatIndianAmount(row.current_value)}</span>
        )}
      </td>
      <td className={cellClass}>{formatIndianAmount(row.previous_value)}</td>
      <td className={cellClass}>{formatIndianAmount(row.since_previous)}</td>
    </tr>
  );
};

const RABillDocument = ({
  mode = 'create',
  bill,
  previousBill,
  onBillChange,
  projects = [],
  projectFinancials = {},
  formConfig,
  departmentRuleInfo,
  departmentRuleLoading,
  projectChains = [],
  projectBGs = [],
  linkedBGs = [],
  customFields = [],
  customValues = {},
  onCustomChange,
  withheldEditor,
  attachmentUploading,
  onAttachmentChange,
  onRemoveAttachment,
  generatedAt,
  issueFlags = [],
  getBGTowardsLabel,
  formatCurrency,
  formatDateIN,
  formatPercentValue,
  divisions = [],
  contractTypes = {},
  invalidField = '',
}) => {
  const [lineMasterOptions, setLineMasterOptions] = useState([]);
  const project = projects.find((item) => Number(item.id) === Number(bill.project_id));
  const contractType = projectFinancials[Number(bill.project_id)]?.contract_type || '';
  const isItemRate = contractType === (contractTypes.ITEM_RATE || 'Item rate % Contract');
  const raNo = bill.ra_no || '';
  const currentBillLabel = bill.ra_ref
    || (bill.division ? `RA-${bill.division}-${raNo || ''}` : `RA-${raNo || ''}`);
  const previousBillLabel = previousBill
    ? (previousBill.ra_ref || `RA-${previousBill.ra_no || ''}`)
    : 'No Previous RA';
  const comparisonBaseLabel = previousBill ? previousBillLabel : 'No Previous RA';
  const projectTitle = project?.name || '';

  const recoveryRates = departmentRuleInfo?.legacy_mode === false && departmentRuleInfo?.rule
    ? {
        security_deposit_pct: departmentRuleInfo.rule.security_deposit_pct,
        gst_pct: departmentRuleInfo.rule.gst_pct,
        income_tax_pct: departmentRuleInfo.rule.income_tax_pct,
        labour_cess_pct: departmentRuleInfo.rule.labour_cess_pct,
      }
    : null;

  const usesConfigurableTemplate = isConfigurableSnapshot(bill);
  const editableTemplateFields = usesConfigurableTemplate
    ? listEditableTemplateFields(bill.ra_template_snapshot)
    : [];
  const templateSections = usesConfigurableTemplate
    ? groupTemplateFieldsBySection(bill.ra_template_snapshot)
    : [];

  const setTemplateInput = (fieldCode, value) => {
    onBillChange((prev) => ({
      ...prev,
      template_field_inputs: {
        ...(prev.template_field_inputs || {}),
        [fieldCode]: value,
      },
    }));
  };

  const getTemplateFieldDisplayValue = (field) => {
    const code = field.fieldCode;
    const inputs = bill.template_field_inputs || {};
    const values = bill.template_field_values || {};
    if (isTemplateInputField(field)) {
      if (inputs[code] != null && inputs[code] !== '') return inputs[code];
      if (values[code] != null && values[code] !== '') return values[code];
      return field.defaultValue ?? '';
    }
    if (values[code] != null && values[code] !== '') return values[code];
    return 0;
  };

  const getPreviousFieldValue = (fieldCode) => {
    const prev = bill.previous_template_values || {};
    if (prev[fieldCode] != null && prev[fieldCode] !== '') return Number(prev[fieldCode]) || 0;
    return 0;
  };

  const canEditAmounts = mode !== 'view' && mode !== 'print';

  const extraUnion = useMemo(
    () => unionAdditionalBillRows(previousBill?.additional_bill_rows, bill.additional_bill_rows),
    [previousBill?.additional_bill_rows, bill.additional_bill_rows],
  );

  useEffect(() => {
    if (mode === 'view' || mode === 'print') return undefined;
    let cancelled = false;
    adminAPI.getRaBillLineOptions()
      .then((res) => {
        if (!cancelled) setLineMasterOptions(res?.data?.data?.options || []);
      })
      .catch(() => {
        if (!cancelled) setLineMasterOptions([]);
      });
    return () => { cancelled = true; };
  }, [mode]);

  const optionsForSection = (sectionCode) => (
    lineMasterOptions.filter((option) => String(option.parentKey || SECTION_CODES.WORK_VALUE) === sectionCode)
  );

  const workRows = [
    ...buildStandardComparisonRows(STANDARD_WORK_VALUE_ROWS, bill, previousBill),
    ...additionalRowsForSection(extraUnion, SECTION_CODES.WORK_VALUE),
  ];
  const recoveryStandard = buildStandardComparisonRows(
    recoveryRowLabelsWithRates(STANDARD_RECOVERY_ROWS, recoveryRates),
    bill,
    previousBill,
  );
  const recoveryRows = [
    ...recoveryStandard.filter((row) => !row.isTotal),
    ...additionalRowsForSection(extraUnion, SECTION_CODES.RECOVERIES),
    ...recoveryStandard.filter((row) => row.isTotal),
  ];
  const totalRows = [
    ...buildStandardComparisonRows(STANDARD_TOTAL_ROWS, bill, previousBill),
    ...additionalRowsForSection(extraUnion, SECTION_CODES.TOTALS),
  ];

  const patchBill = (patch) => onBillChange((prev) => ({ ...prev, ...patch }));

  const updateExtraRowValue = (row, value) => {
    onBillChange((prev) => {
      const existing = safeArray(prev.additional_bill_rows);
      const idx = existing.findIndex((item) => (
        (row.row_id && item.row_id === row.row_id) || (row.row_code && item.row_code === row.row_code)
      ));
      const nextRow = createAdditionalBillRow({
        ...row,
        current_value: value,
      });
      if (idx >= 0) {
        const next = existing.slice();
        next[idx] = { ...existing[idx], ...nextRow, current_value: value };
        return { ...prev, additional_bill_rows: next };
      }
      return { ...prev, additional_bill_rows: [...existing, nextRow] };
    });
  };

  const updateExtraRowMeta = (row, patch) => {
    onBillChange((prev) => {
      const existing = safeArray(prev.additional_bill_rows);
      const idx = existing.findIndex((item) => (
        (row.row_id && item.row_id === row.row_id) || (row.row_code && item.row_code === row.row_code)
      ));
      const nextRow = createAdditionalBillRow({
        ...(idx >= 0 ? existing[idx] : row),
        ...row,
        ...patch,
        row_id: row.row_id,
      });
      if (idx >= 0) {
        const next = existing.slice();
        next[idx] = { ...existing[idx], ...nextRow };
        return { ...prev, additional_bill_rows: next };
      }
      return { ...prev, additional_bill_rows: [...existing, nextRow] };
    });
  };

  const addExtraRow = (sectionCode) => {
    onBillChange((prev) => ({
      ...prev,
      additional_bill_rows: [
        ...safeArray(prev.additional_bill_rows),
        createAdditionalBillRow({
          description: '',
          section_code: sectionCode,
          current_value: 0,
        }),
      ],
    }));
  };

  const fieldClass = (key) => (invalidField === key ? 'ra-field-invalid' : '');

  const renderComparisonTable = (title, headerLabel, rows, sectionCode) => (
    <section className="ra-section-card">
      <div className="ra-section-card__head">
        <h3>{title}</h3>
      </div>
      <div className="table-scroll">
        <table className="ra-compare-table">
          <thead>
            <tr>
              <th className="desc-col" align="left" style={{ textAlign: 'left' }}>{headerLabel}</th>
              <th className="amount current-col">Current Bill ({currentBillLabel || 'RA'}) (Rs.)</th>
              <th className="amount">Previous Bill ({previousBillLabel}) (Rs.)</th>
              <th className="amount">Since Previous Bill (Rs.)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExtra = !row.fieldKey;
              return (
                <ComparisonRow
                  key={row.row_code || row.row_id}
                  row={row}
                  currentEditable={Boolean(row.editable) && mode !== 'view' && mode !== 'print'}
                  descriptionEditable={isExtra && mode !== 'view' && mode !== 'print'}
                  descriptionOptions={optionsForSection(sectionCode)}
                  invalidDescription={invalidField === `extra_row_${row.row_id || row.row_code}`}
                  onCurrentChange={(value) => {
                    if (row.fieldKey) patchBill({ [row.fieldKey]: value });
                    else updateExtraRowValue(row, value);
                  }}
                  onDescriptionChange={(patch) => updateExtraRowMeta(row, patch)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      {mode === 'create' || mode === 'edit' ? (
        <div className="add-row-bar">
          <button type="button" className="btn-secondary btn-sm" onClick={() => addExtraRow(sectionCode)}>
            + Add Row
          </button>
        </div>
      ) : null}
    </section>
  );

  const paidValue = Number(bill.paid_value || 0);
  const netPayable = Number(bill.net_payable || bill.net_payment_received || 0);
  const balanceValue = bill.balance_value != null && bill.balance_value !== ''
    ? Number(bill.balance_value)
    : Math.max(0, netPayable - paidValue);
  const paymentStatus = bill.payment_status || 'Unpaid';
  const payments = safeArray(bill.payments);
  const canEditPayments = mode === 'edit';

  return (
    <div className="ra-sheet">
      <section className="ra-create-card">
        <div className="ra-create-meta">
          {bill.project_id ? (
            <>
              {projectTitle ? <span className="ra-meta-chip"><strong>{projectTitle}</strong></span> : null}
              <span className="ra-meta-chip">RA Bill: <strong>{currentBillLabel || '—'}</strong></span>
              {generatedAt ? <span className="ra-meta-chip">Generated: <strong>{generatedAt}</strong></span> : null}
              <span className="ra-meta-chip">Comparison Base: <strong>{comparisonBaseLabel}</strong></span>
            </>
          ) : (
            <span className="ra-meta-chip">Select a project to prepare this RA bill</span>
          )}
        </div>

        <div className="ra-create-fields">
          <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
            {({ readOnly }) => (
              <select
                className={fieldClass('project_id')}
                data-ra-field="project_id"
                value={bill.project_id}
                disabled={readOnly || mode === 'view'}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const nextType = projectFinancials[Number(projectId)]?.contract_type || '';
                  patchBill({
                    project_id: projectId,
                    division: nextType === (contractTypes.ITEM_RATE || 'Item rate % Contract') ? bill.division : '',
                  });
                }}
              >
                <option value="">Select Project</option>
                {projects.filter((p) => p.status === 'Active').map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          </ConfiguredField>
          {isItemRate && (
            <div className="form-group">
              <label>Division / Category <span className="req">*</span></label>
              <select
                className={fieldClass('division')}
                data-ra-field="division"
                value={bill.division || ''}
                onChange={(e) => patchBill({ division: e.target.value })}
              >
                <option value="">Select Division</option>
                {divisions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
          <ConfiguredField fieldKey="agreement_no" formConfig={formConfig} defaultLabel="Agreement No." defaultRequired>
            {({ readOnly, placeholder }) => (
              <input
                className={fieldClass('agreement_no')}
                data-ra-field="agreement_no"
                type="text"
                value={bill.agreement_no || ''}
                disabled={readOnly}
                placeholder={placeholder || 'As per contract'}
                onChange={(e) => patchBill({ agreement_no: e.target.value })}
              />
            )}
          </ConfiguredField>
          <div className="form-group">
            <label>RA Bill Date <span className="req">*</span></label>
            <input
              className={fieldClass('ra_bill_date')}
              data-ra-field="ra_bill_date"
              type="date"
              value={bill.ra_bill_date || ''}
              onChange={(e) => patchBill({ ra_bill_date: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Certified Date <span className="req">*</span></label>
            <input
              className={fieldClass('certified_date')}
              data-ra-field="certified_date"
              type="date"
              value={bill.certified_date || ''}
              onChange={(e) => patchBill({ certified_date: e.target.value })}
            />
          </div>
          <ConfiguredField fieldKey="status" formConfig={formConfig} defaultLabel="Status" defaultRequired>
            {({ readOnly }) => (
              <select
                className={fieldClass('status')}
                data-ra-field="status"
                value={bill.status || 'Draft'}
                disabled={readOnly}
                onChange={(e) => patchBill({ status: e.target.value })}
              >
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Paid">Paid</option>
              </select>
            )}
          </ConfiguredField>
          <div className="form-group">
            <label>Period From <span className="req">*</span></label>
            <input
              className={fieldClass('period_from')}
              data-ra-field="period_from"
              type="date"
              value={bill.period_from || ''}
              onChange={(e) => patchBill({ period_from: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Period To <span className="req">*</span></label>
            <input
              className={fieldClass('period_to')}
              data-ra-field="period_to"
              type="date"
              value={bill.period_to || ''}
              onChange={(e) => patchBill({ period_to: e.target.value })}
            />
          </div>
          <ConfiguredField className="form-group span-full" fieldKey="remarks" formConfig={formConfig} defaultLabel="Remarks" defaultRequired>
            {({ readOnly, placeholder }) => (
              <input
                className={fieldClass('remarks')}
                data-ra-field="remarks"
                type="text"
                value={bill.remarks || ''}
                disabled={readOnly}
                placeholder={placeholder || 'Enter remarks'}
                onChange={(e) => patchBill({ remarks: e.target.value })}
              />
            )}
          </ConfiguredField>
        </div>
      </section>

      {bill.project_id && (
        <div className="ra-dept-banner">
          <p>{mode === 'create' ? 'Department Rule Resolution' : 'Bill Calculation Snapshot'}</p>
          {mode === 'create' ? (
            departmentRuleLoading ? (
              <p>Resolving FY rule...</p>
            ) : departmentRuleInfo ? (
              <>
                <p>
                  Department: {departmentRuleInfo?.department?.name || 'Not mapped'} | FY: {departmentRuleInfo?.financial_year || '-'}
                </p>
                {usesConfigurableTemplate ? (
                  <p style={{ color: '#166534' }}>
                    Calculation uses{' '}
                    {departmentRuleInfo?.ra_template_source === 'project' ? 'project' : 'department'}{' '}
                    template{' '}
                    <strong>
                      {departmentRuleInfo.ra_template?.name || departmentRuleInfo.ra_template?.code || bill.ra_template_code}
                      {(departmentRuleInfo.ra_template?.version ?? bill.ra_template_version) != null
                        ? ` v${departmentRuleInfo.ra_template?.version ?? bill.ra_template_version}`
                        : ''}
                    </strong>
                    {' '}— CPWD sheet is hidden; this template drives the bill form and will be frozen on save.
                  </p>
                ) : departmentRuleInfo?.ra_template ? (
                  <p style={{ color: '#6b7280' }}>
                    RA template ({departmentRuleInfo?.ra_template_source === 'project' ? 'project' : 'department'}):{' '}
                    {departmentRuleInfo.ra_template.name || departmentRuleInfo.ra_template.code} (legacy CPWD calculation).
                  </p>
                ) : (
                  <p style={{ color: '#6b7280' }}>No project or department RA template assigned — using CPWD calculation.</p>
                )}
                {(() => {
                  const rule = departmentRuleInfo?.rule;
                  const fy = departmentRuleInfo?.financial_year || '—';
                  if (rule) {
                    return (
                      <p style={{ color: '#166534' }}>
                        FY recovery rates ({fy}): SD {Number(rule.security_deposit_pct ?? 2.5)}% · GST {Number(rule.gst_pct ?? 2)}% · IT {Number(rule.income_tax_pct ?? 2)}% · Labour cess {Number(rule.labour_cess_pct ?? 1)}%
                      </p>
                    );
                  }
                  return (
                    <p style={{ color: '#b45309' }}>
                      FY recovery rates for {fy}: not configured on department — template uses default % (SD 2.5 / GST 2 / IT 2 / Labour cess 1). Add rates under Admin → Departments.
                    </p>
                  );
                })()}
              </>
            ) : (
              <p>No department/FY rule details available.</p>
            )
          ) : (
            <>
              <p>
                Department: {bill.department_name || bill.department_code || '—'}
                {bill.financial_year ? ` | FY: ${bill.financial_year}` : ''}
              </p>
              {usesConfigurableTemplate ? (
                <p style={{ color: '#166534' }}>
                  Frozen RA template: {bill.ra_template_code || bill.ra_template_id}
                  {bill.ra_template_version != null ? ` v${bill.ra_template_version}` : ''}
                  {' '}(amounts stay fixed until you edit this bill).
                </p>
              ) : (
                <p style={{ color: '#6b7280' }}>
                  No frozen configurable template on this bill — CPWD / stored values apply.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="ra-stat-row">
        <div className="ra-stat-card">
          <label>No. of bills paid by dept.</label>
          <strong>{bill.bills_paid_by_dept ?? 0}</strong>
        </div>
        <div className="ra-stat-card">
          <label>This R.A Bill No.</label>
          <strong>{raNo || '—'}</strong>
        </div>
        <div className="ra-stat-card">
          <label>% Workdone</label>
          <strong>{formatPercentValue(bill.percent_workdone)}</strong>
        </div>
        {bill.division ? (
          <div className="ra-stat-card">
            <label>Division / Category</label>
            <strong>{bill.division}</strong>
          </div>
        ) : null}
        {usesConfigurableTemplate ? (
          <div className="ra-stat-card">
            <label>Template</label>
            <strong>{bill.ra_template_code || 'Configurable'}{bill.ra_template_version != null ? ` v${bill.ra_template_version}` : ''}</strong>
          </div>
        ) : null}
      </div>

      {usesConfigurableTemplate ? (
        <>
          <section className="ra-section-card ra-follow-on-card">
            <div className="ra-section-card__head">
              <h3>RA bill dependency chain</h3>
            </div>
            <div className="ra-follow-on-body">
              <div className="ra-chain-visual" aria-label="RA dependency chain">
                <span className={`ra-chain-node${Number(bill.ra_no || 1) === 1 ? ' is-current' : ''}`}>RA-1</span>
                <span className="ra-chain-arrow">→</span>
                <span className={`ra-chain-node${Number(bill.ra_no || 1) === 2 ? ' is-current' : ''}`}>RA-2</span>
                <span className="ra-chain-arrow">→</span>
                <span className={`ra-chain-node${Number(bill.ra_no || 1) === 3 ? ' is-current' : ''}`}>RA-3</span>
                <span className="ra-chain-arrow">→</span>
                <span className="ra-chain-node is-more">…</span>
                {Number(bill.ra_no || 1) > 3 ? (
                  <>
                    <span className="ra-chain-arrow">→</span>
                    <span className="ra-chain-node is-current">{currentBillLabel || `RA-${bill.ra_no}`}</span>
                  </>
                ) : null}
              </div>
              <p className="ra-chain-rule">
                Each bill depends only on the <strong>immediate previous</strong> bill:
                {' '}RA-2 ← RA-1 · RA-3 ← RA-2 · RA-4 ← RA-3 (never skips back to RA-1).
              </p>

              {!previousBill || Number(bill.ra_no || 1) <= 1 ? (
                <>
                  <p>
                    <strong>This is RA-1 (first bill).</strong> No dependency yet — Previous column is <strong>0</strong>.
                    On save, field values are frozen so <strong>RA-2</strong> can read them.
                  </p>
                  <ol className="ra-follow-on-steps">
                    <li>Same template format is reused for every RA number.</li>
                    <li>When you create RA-2, Previous column loads from <strong>this</strong> bill&apos;s frozen values.</li>
                    <li>When you later create RA-3, Previous loads from <strong>RA-2</strong> (not RA-1).</li>
                  </ol>
                </>
              ) : (
                <>
                  <p>
                    <strong>This is {currentBillLabel}</strong> — depends on{' '}
                    <strong>{previousBillLabel}</strong> only
                    {Number(bill.ra_no || 1) >= 3
                      ? ` (not on earlier bills like RA-${Number(bill.ra_no) - 2}).`
                      : '.'}
                  </p>
                  <ol className="ra-follow-on-steps">
                    <li>
                      <strong>Previous</strong> = frozen field values from {previousBillLabel}.
                    </li>
                    <li>
                      <strong>Current</strong> = this bill&apos;s inputs / calculations.
                    </li>
                    <li>
                      <strong>Since Previous</strong> = Current − Previous (progress since {previousBillLabel}).
                    </li>
                    <li>
                      After save, the <strong>next</strong> bill (RA-{(Number(bill.ra_no || 1) + 1)}) will depend on <strong>this</strong> bill.
                    </li>
                  </ol>
                  {Object.keys(bill.previous_template_values || {}).length > 0 ? (
                    <p className="ra-follow-on-ok">
                      Dependency loaded: {Object.keys(bill.previous_template_values).length} field values from {previousBillLabel}.
                    </p>
                  ) : (
                    <p className="ra-follow-on-warn">
                      No frozen template field map on {previousBillLabel} yet. Save that bill with the configurable template so the chain can carry values forward.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>

          <section className="ra-section-card">
            <div className="ra-section-card__head">
              <h3>FY Recovery Rates (department)</h3>
              <p className="muted" style={{ margin: 0 }}>
                Default template rates from the department financial-year rule. Used by SD / GST / IT / Labour cess calculations when rate source is Department.
              </p>
            </div>
            <div className="table-scroll">
              <table className="ra-compare-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th className="amount">Rate %</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rule = departmentRuleInfo?.rule || bill.department_rule_snapshot;
                    const found = Boolean(rule) && departmentRuleInfo?.legacy_mode === false;
                    const rows = [
                      { label: 'Security Deposit %', key: 'security_deposit_pct', fallback: 2.5 },
                      { label: 'GST TDS %', key: 'gst_pct', fallback: 2 },
                      { label: 'Income Tax TDS %', key: 'income_tax_pct', fallback: 2 },
                      { label: 'Labour Cess %', key: 'labour_cess_pct', fallback: 1 },
                    ];
                    return rows.map((r) => (
                      <tr key={r.key}>
                        <td className="desc-col" align="left" style={{ textAlign: 'left' }}>{r.label}</td>
                        <td className="amount">
                          <span className="readonly-amount">
                            {formatIndianAmount(Number(rule?.[r.key] ?? r.fallback))}
                          </span>
                        </td>
                        <td>{found ? `Department FY ${departmentRuleInfo?.financial_year || bill.financial_year || ''}` : 'Default (no FY rule)'}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ra-section-card">
            <div className="ra-section-card__head">
              <h3>
                {bill.ra_template_snapshot?.name || bill.ra_template_code || 'RA Bill Template'}
              </h3>
              <p className="muted" style={{ margin: 0 }}>
                Enter values in editable rows. Calculated rows update from your template rules.
                {previousBill
                  ? ` Following ${previousBillLabel}: Previous column is auto-filled; Since Previous = Current − Previous.`
                  : ' First bill — Previous column is zero; RA-2+ will follow this bill in the same format.'}
              </p>
            </div>
            <div className="table-scroll">
              <table className="ra-compare-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th className="amount">Current ({currentBillLabel}) (Rs.)</th>
                    <th className="amount">Previous ({previousBillLabel}) (Rs.)</th>
                    <th className="amount">Since Previous (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {templateSections.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="center muted">No visible fields on this template.</td>
                    </tr>
                  ) : templateSections.map((section) => (
                    <React.Fragment key={section.key}>
                      <tr className="ra-template-section-row">
                        <td colSpan={4} className="ra-template-section-label">
                          {section.sectionName}
                        </td>
                      </tr>
                      {section.fields.map((field) => {
                        const code = field.fieldCode;
                        const editable = canEditAmounts && isTemplateInputField(field);
                        const currentVal = getTemplateFieldDisplayValue(field);
                        const prevVal = getPreviousFieldValue(code);
                        const since = Number(currentVal || 0) - Number(prevVal || 0);
                        const type = String(field.fieldType || 'INPUT').toUpperCase();
                        return (
                          <tr key={code}>
                            <td className="desc-col" align="left" style={{ textAlign: 'left' }}>
                              {field.fieldLabel || code}
                              {type !== 'INPUT' && type !== 'LOOKUP' ? (
                                <span className="ra-template-field-tag">{type === 'PREVIOUS_RA' ? 'Previous RA' : 'Calculated'}</span>
                              ) : null}
                            </td>
                            <td className={`amount current-col${editable ? ' editable-cell' : ''}`}>
                              {editable ? (
                                <IndianAmountInput
                                  value={currentVal}
                                  onCommit={(v) => setTemplateInput(code, v)}
                                />
                              ) : (
                                <span className="readonly-amount">{formatIndianAmount(currentVal)}</span>
                              )}
                            </td>
                            <td className="amount">{formatIndianAmount(prevVal)}</td>
                            <td className="amount">{formatIndianAmount(since)}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {canEditAmounts && editableTemplateFields.length === 0 ? (
            <p className="muted">This template has no manual input fields — all values are calculated.</p>
          ) : null}
        </>
      ) : (
        <>
          {renderComparisonTable('Work Done and Bill Value (Current vs Previous Bill)', 'Description', workRows, SECTION_CODES.WORK_VALUE)}
          {renderComparisonTable('Deductions - Recoveries Breakdown (Current vs Previous Bill)', 'Particulars', recoveryRows, SECTION_CODES.RECOVERIES)}
          {renderComparisonTable('Bill Totals (Current vs Previous Bill)', 'Particulars', totalRows, SECTION_CODES.TOTALS)}
        </>
      )}

      <details className="collapsible" open={!usesConfigurableTemplate}>
        <summary>Withheld Breakdown</summary>
        <div className="collapsible-body">
          {withheldEditor}
        </div>
      </details>

      <section className="ra-section-card">
        <div className="ra-section-card__head">
          <h3>Payment Status</h3>
        </div>
        <div className="table-scroll">
          <table className="ra-compare-table">
            <thead>
              <tr><th>Field</th><th>Value</th></tr>
            </thead>
            <tbody>
              <tr><td>Paid Value (Rs.)</td><td className="amount">{formatIndianAmount(paidValue)}</td></tr>
              <tr><td>Balance Value (Rs.)</td><td className="amount">{formatIndianAmount(balanceValue)}</td></tr>
              <tr><td>Last Payment Received Date</td><td>{formatDateIN(bill.payment_received_date) || '—'}</td></tr>
              <tr><td>Payment Status</td><td>{paymentStatus}</td></tr>
              <tr><td>Active Risk Flags</td><td>{issueFlags.length ? issueFlags.join(', ') : 'No active flags'}</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="ra-section-card">
        <div className="ra-section-card__head">
          <h3>Payment Details</h3>
        </div>
        <div className="table-scroll">
          <table className="ra-compare-table">
            <thead>
              <tr>
                <th>Sl. No</th>
                <th>Date</th>
                <th className="amount">Amount (Rs.)</th>
                <th>Authority</th>
                <th>Voucher No</th>
                <th>CM Book</th>
                <th>Cash Book</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="center muted">
                    {canEditPayments ? 'No payment records added.' : 'Payments can be added after the RA Bill is saved.'}
                  </td>
                </tr>
              ) : payments.map((payment, index) => (
                <tr key={payment.id || index}>
                  <td className="center">{index + 1}</td>
                  <td>{formatDateIN(payment.payment_date)}</td>
                  <td className="amount">{formatIndianAmount(payment.amount_received)}</td>
                  <td>{payment.paying_authority || '-'}</td>
                  <td>{payment.voucher_no || '-'}</td>
                  <td>{`${payment.cm_book_no || '-'}${payment.cm_book_page_nos ? ` / ${payment.cm_book_page_nos}` : ''}`}</td>
                  <td>{`${payment.cash_book_no || '-'}${payment.cash_book_page_no ? ` / ${payment.cash_book_page_no}` : ''}`}</td>
                  <td>{payment.remarks || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="ra-section-card">
        <div className="ra-section-card__head">
          <h3>Details of Bank Guarantees Submitted</h3>
        </div>
        <div className="table-scroll">
          <table className="ra-compare-table">
            <thead>
              <tr>
                <th>Sl.No.</th>
                <th>Towards</th>
                <th>Instrument</th>
                <th>Date</th>
                <th className="amount">Amount (Rs.)</th>
                <th>Bank</th>
                <th>Validity date</th>
                <th>Attached details</th>
              </tr>
            </thead>
            <tbody>
              {linkedBGs.length === 0 ? (
                <tr>
                  <td className="center">-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td className="amount">-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>No linked BG details</td>
                </tr>
              ) : linkedBGs.map((bg, index) => (
                <tr key={bg.id || index}>
                  <td className="center">{index + 1}</td>
                  <td>{getBGTowardsLabel(bg)}</td>
                  <td>{bg.bg_no || '-'}</td>
                  <td>{formatDateIN(bg.issue_date)}</td>
                  <td className="amount">{formatIndianAmount(bg.amount)}</td>
                  <td>{bg.bank || '-'}</td>
                  <td>{formatDateIN(bg.expiry_date)}</td>
                  <td>{safeArray(bg.attachments).map((file) => file?.name).filter(Boolean).join(', ') || 'No attachments'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <details className="collapsible">
        <summary>Bill Controls</summary>
        <div className="collapsible-body form-grid">
          <div className="form-group">
            <label>Payment Due Date</label>
            <input type="date" value={bill.payment_due_date || ''} onChange={(e) => patchBill({ payment_due_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Billing Basis Type</label>
            <select value={bill.billing_basis_type || 'Quantity Based'} onChange={(e) => patchBill({ billing_basis_type: e.target.value })}>
              <option value="Quantity Based">Quantity Based</option>
              <option value="Milestone Based">Milestone Based</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
          <div className="form-group">
            <label>Dependency Status</label>
            <select value={bill.billing_dependency_status || 'Clear'} onChange={(e) => patchBill({ billing_dependency_status: e.target.value })}>
              <option value="Clear">Clear</option>
              <option value="At Risk">At Risk</option>
              <option value="Blocked">Blocked</option>
            </select>
          </div>
          <div className="form-group">
            <label>Actual Total Recovered (optional)</label>
            <input
              type="number"
              min="0"
              value={bill.actual_total_recovered_amount ?? ''}
              onChange={(e) => patchBill({ actual_total_recovered_amount: e.target.value })}
            />
          </div>
          <div className="form-group span-full">
            <label>Billing Hold Reason</label>
            <textarea rows={3} value={bill.billing_hold_reason || ''} onChange={(e) => patchBill({ billing_hold_reason: e.target.value })} />
          </div>
          <label className="ra-checkbox-row">
            <input type="checkbox" checked={Boolean(bill.withheld_due_to_milestone_flag)} onChange={(e) => patchBill({ withheld_due_to_milestone_flag: e.target.checked })} />
            Withheld due to milestone
          </label>
          <label className="ra-checkbox-row">
            <input type="checkbox" checked={Boolean(bill.withheld_due_to_eot_flag)} onChange={(e) => patchBill({ withheld_due_to_eot_flag: e.target.checked })} />
            Withheld due to EOT
          </label>
        </div>
      </details>

      <details className="collapsible">
        <summary>Linking &amp; References</summary>
        <div className="collapsible-body form-grid">
          <div className="form-group">
            <label>Primary Correspondence</label>
            <select value={bill.linked_correspondence || ''} onChange={(e) => patchBill({ linked_correspondence: e.target.value, primary_correspondence_chain_id: e.target.value || null })}>
              <option value="">None</option>
              {projectChains.map((chain) => (
                <option key={chain.id} value={chain.id}>{chain.first_letter_ref || `#${chain.id}`} - {chain.subject}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Related Correspondence IDs</label>
            <input
              type="text"
              value={safeArray(bill.related_correspondence_ids).join(', ')}
              placeholder="e.g. 12, 15"
              onChange={(e) => {
                const ids = e.target.value.split(',').map((part) => Number(part.trim())).filter((n) => Number.isFinite(n) && n > 0);
                patchBill({ related_correspondence_ids: ids });
              }}
            />
          </div>
          <div className="form-group">
            <label>Linked Milestone ID</label>
            <input type="number" min="0" value={bill.linked_milestone_id || ''} onChange={(e) => patchBill({ linked_milestone_id: e.target.value === '' ? '' : Number(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Linked EOT ID</label>
            <input type="number" min="0" value={bill.linked_eot_id || ''} onChange={(e) => patchBill({ linked_eot_id: e.target.value === '' ? '' : Number(e.target.value) })} />
          </div>
          <div className="form-group">
            <label>Primary BG</label>
            <select value={bill.primary_bg_id || ''} onChange={(e) => patchBill({ primary_bg_id: e.target.value })}>
              <option value="">None</option>
              {projectBGs.map((bg) => (
                <option key={bg.id} value={bg.id}>{bg.bg_no} - {bg.bank}</option>
              ))}
            </select>
          </div>
          <div className="form-group span-full">
            <label>Linked BGs</label>
            <div className="ra-bg-list">
              {projectBGs.length === 0 ? (
                <span className="muted">Select a project with BGs to map them here.</span>
              ) : projectBGs.map((bg) => {
                const checked = safeArray(bill.linked_bg_ids).map(Number).includes(Number(bg.id));
                return (
                  <label key={bg.id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = new Set(safeArray(bill.linked_bg_ids).map(Number));
                        if (e.target.checked) current.add(Number(bg.id));
                        else current.delete(Number(bg.id));
                        patchBill({ linked_bg_ids: Array.from(current) });
                      }}
                    />
                    <span>{bg.bg_no} | {getBGTowardsLabel(bg)} | {bg.bank} | {formatCurrency(bg.amount)}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </details>

      <details className="collapsible">
        <summary>Attachments</summary>
        <div className="collapsible-body">
          <div className="form-group">
            <label>Upload files</label>
            <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={onAttachmentChange} disabled={attachmentUploading} />
          </div>
          {attachmentUploading && <p className="muted">Processing upload...</p>}
          {safeArray(bill.attachments).length > 0 && (
            <div className="ra-file-chips">
              {bill.attachments.map((file, idx) => (
                <span key={idx} className="ra-file-chip">
                  {file?.name}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => onRemoveAttachment(idx)}>✕</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </details>

      {formConfig?.customFieldsEnabled && (
        <details className="collapsible">
          <summary>Additional Information</summary>
          <div className="collapsible-body">
            <DynamicFieldsSection
              fields={customFields}
              values={customValues}
              onChange={onCustomChange}
              title="Additional fields"
            />
          </div>
        </details>
      )}
    </div>
  );
};

export default RABillDocument;
